import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LeadThankYouCard from "@/components/home/LeadThankYou";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const headcountRanges = [
  "1 - 10",
  "11 - 50",
  "51 - 200",
  "201 - 500",
  "501 - 1000",
  "1000+",
];

const industries = [
  "Agricultura y Agroindustria",
  "Alimentos y Bebidas",
  "Banca y Servicios Financieros",
  "Comercio / Retail",
  "Construcción e Infraestructura",
  "Consultoría",
  "Educación",
  "Energía y Servicios Públicos",
  "Farmacéutica y Ciencias de la Vida",
  "Gobierno y Sector Público",
  "Hotelería y Turismo",
  "Inmobiliario",
  "Legal",
  "Logística y Transporte",
  "Manufactura",
  "Marketing y Publicidad",
  "Medios y Entretenimiento",
  "Minería",
  "ONG y Sin Fines de Lucro",
  "Recursos Humanos / Reclutamiento",
  "Restaurantes y Servicio de Comidas",
  "Salud y Servicios Médicos",
  "Seguros",
  "Servicios Profesionales",
  "Tecnología / Software / TI",
  "Telecomunicaciones",
  "Textil y Moda",
  "Otro",
];

interface LeadFormCardProps {
  title?: string;
  description?: string;
  onSuccess?: () => void;
  compact?: boolean;
  source?: string;
}

export const LeadFormCard: React.FC<LeadFormCardProps> = ({
  title = "Empiece a reclutar con tecnología hoy mismo",
  description = "Complete el formulario y un especialista le muestra cómo Nexus ATS transforma su proceso de reclutamiento.",
  onSuccess,
  compact = false,
  source = "unknown",
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    cedulaJuridica: "",
    email: "",
    phone: "",
    headcount: "",
    industry: "",
    website: "", // honeypot
  });
  const [cedulaError, setCedulaError] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const update = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  // Formato automático X-XXX-XXXXXX mientras se escribe
  const formatCedula = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const parts = [digits.slice(0, 1), digits.slice(1, 4), digits.slice(4, 10)].filter(Boolean);
    return parts.join("-");
  };

  const isValidCedula = (value: string) => /^\d{10}$/.test(value.replace(/\D/g, ""));

  const handleCedulaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    update("cedulaJuridica", formatCedula(e.target.value));
    setCedulaError(false);
  };

  useEffect(() => {
    if (submitted) {
      navigate("/gracias", { state: { leadThankYou: true } });
    }
  }, [navigate, submitted]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.website) return;
    if (!form.name || !form.company || !form.cedulaJuridica || !form.email || !form.phone) {
      toast({ title: "Complete los campos requeridos", variant: "destructive" });
      return;
    }
    if (!isValidCedula(form.cedulaJuridica)) {
      setCedulaError(true);
      return;
    }
    if (!confirmed) {
      toast({ title: "Confirme que representa a una empresa", variant: "destructive" });
      return;
    }
    setConfirmOpen(true);
  };

  const sendLead = async () => {
    setSubmitting(true);
    try {
      await supabase.functions.invoke("send-contact-email", {
        body: {
          name: form.name,
          email: form.email,
          company: form.company,
          cedulaJuridica: form.cedulaJuridica,
          phone: form.phone,
          headcount: form.headcount || undefined,
          industry: form.industry || undefined,
          source,
          route: typeof window !== "undefined" ? window.location.pathname : undefined,
          profile: "empresa",
          type: "lead",
          message: `Empresa: ${form.company}\nCédula jurídica: ${form.cedulaJuridica}\nCantidad de colaboradores: ${form.headcount || "No especificado"}\nSector o industria: ${form.industry || "No especificado"}\nOrigen: ${source}\nSolicita asesoría personalizada.`,
        },
      });
      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      toast({
        title: "Error al enviar",
        description: "Por favor intente nuevamente o escríbanos a soporte@nexushrcr.com",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <LeadThankYouCard
        compact={compact}
        footer={
          <button
            type="button"
            onClick={() => {
              if (window.history.state?.leadThankYou) {
                window.history.back();
              }
              setSubmitted(false);
              setForm({ name: "", company: "", cedulaJuridica: "", email: "", phone: "", headcount: "", industry: "", website: "" });
              setCedulaError(false);
              setConfirmed(false);
            }}
            className="text-sm text-[#620AE2] hover:underline font-medium"
          >
            Enviar otra solicitud
          </button>
        }
      />
    );
  }

  return (
    <div className={cn("bg-white rounded-2xl p-6 md:p-10", !compact && "shadow-2xl")}>
      <div className="text-center mb-8">
        <h2 className="font-exo font-bold mb-3" style={{ fontSize: "28px", color: "#1A1A2E" }}>
          {title}
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
          className="absolute left-[-9999px] opacity-0"
          aria-hidden="true"
        />

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="nlf-name" className="sr-only">Nombre completo (requerido)</label>
            <Input
              id="nlf-name"
              placeholder="Nombre completo *"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              aria-required="true"
              autoComplete="name"
            />
          </div>
          <div>
            <label htmlFor="nlf-company" className="sr-only">Nombre de la empresa (requerido)</label>
            <Input
              id="nlf-company"
              placeholder="Nombre de la empresa *"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              required
              aria-required="true"
              autoComplete="organization"
            />
          </div>
          <div>
            <label htmlFor="nlf-cedula" className="sr-only">Cédula jurídica de la empresa (requerido)</label>
            <Input
              id="nlf-cedula"
              placeholder="Cédula jurídica de la empresa * (Ej: 3-101-123456)"
              value={form.cedulaJuridica}
              onChange={handleCedulaChange}
              required
              aria-required="true"
              aria-invalid={cedulaError}
              aria-describedby={cedulaError ? "nlf-cedula-error" : undefined}
              inputMode="numeric"
              autoComplete="off"
            />
            {cedulaError && (
              <p id="nlf-cedula-error" role="alert" className="mt-1 text-xs text-destructive">
                Ingresa una cédula jurídica válida (10 dígitos).
              </p>
            )}
          </div>
          <div>
            <label htmlFor="nlf-email" className="sr-only">Email corporativo (requerido)</label>
            <Input
              id="nlf-email"
              type="email"
              placeholder="Email corporativo *"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              aria-required="true"
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="nlf-phone" className="sr-only">Número de teléfono (requerido)</label>
            <Input
              id="nlf-phone"
              type="tel"
              placeholder="Número de teléfono *"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              required
              aria-required="true"
              autoComplete="tel"
            />
          </div>

          <div>
            <label htmlFor="nlf-headcount" className="sr-only">Cantidad de colaboradores</label>
            <Select value={form.headcount} onValueChange={(v) => update("headcount", v)}>
              <SelectTrigger id="nlf-headcount" aria-label="Cantidad de colaboradores">
                <SelectValue placeholder="Cantidad de colaboradores" />
              </SelectTrigger>
              <SelectContent>
                {headcountRanges.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative">
            <label htmlFor="nlf-industry" className="sr-only">Sector o industria</label>
            <select
              id="nlf-industry"
              value={form.industry}
              onChange={(e) => update("industry", e.target.value)}
              className={cn(
                "flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none pr-10"
              )}
              aria-label="Sector o industria"
            >
              <option value="" disabled>
                Sector o industria
              </option>
              {industries.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
          </div>

        </div>

        <p className="text-xs text-muted-foreground" id="nlf-required-note">
          Los campos marcados con * son obligatorios.
        </p>

        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
          <p id="nlf-enterprise-notice" className="text-xs leading-relaxed text-muted-foreground">
            Este es un canal exclusivo para empresas. Nuestros servicios de reclutamiento están
            dirigidos únicamente a personas jurídicas. Si eres una persona buscando empleo, este
            formulario no es para ti.
          </p>
          <Label
            htmlFor="nlf-enterprise-confirm"
            className="flex items-start gap-3 text-xs font-normal leading-relaxed text-muted-foreground cursor-pointer"
          >
            <Checkbox
              id="nlf-enterprise-confirm"
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              aria-describedby="nlf-enterprise-notice"
              className="mt-0.5 h-5 w-5 shrink-0"
            />
            <span>
              Confirmo que represento a una empresa y deseo enviar esta información.
            </span>
          </Label>
        </div>

        <Button
          type="submit"
          disabled={submitting || !confirmed}
          className="w-full h-12 font-semibold rounded-md bg-white text-[#620AE2] border-[1.5px] border-[#620AE2] hover:bg-[#620AE2] hover:text-white hover:border-[#620AE2] transition-all duration-200"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Solicitar asesoría personalizada
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Sin compromiso. Su información es confidencial.
        </p>
      </form>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-exo text-lg font-bold text-foreground">
              Confirmar envío
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
              ¿Estás seguro de que deseas enviar tu información? Recuerda que este canal es
              exclusivo para empresas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={sendLead}
              className="font-semibold"
            >
              Sí, enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const NexusLeadForm: React.FC = () => {
  return (
    <section
      id="lead-form"
      className="relative"
      style={{
        paddingTop: "64px",
        paddingBottom: "64px",
        background: "linear-gradient(135deg, #1A1060 0%, #2D1B8E 40%, #620AE2 100%)",
      }}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <LeadFormCard source="hero" />
        </div>
      </div>
    </section>
  );
};

export default NexusLeadForm;
